import BillingItemsDB from 'src/fb/billingItems-db';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';

import { createLookup } from './lookup';

/**
 * Names for what a hold task is about, from what the app already holds. Ids only reach a task, so
 * the worklist asks here for the words a biller reads.
 */

/** @param {string} clientId @returns {string|null} */
export const clientName = (clientId) => useClientsStore().clients
  .find((client) => client.id === clientId)?.clientName ?? null;

/** @param {string} treatmentServiceId @returns {string|null} */
export const treatmentServiceName = (treatmentServiceId) => useAuthStore().treatmentServices
  .find((service) => service.id === treatmentServiceId)?.serviceName ?? null;

/** Treatment services are bound lazily; a name lookup needs them bound first. */
export const ensureTreatmentServicesBound = () => useAuthStore().ensureTreatmentServicesBound();

/** The tenant every lookup here is scoped by. */
export const currentClinicId = () => useAuthStore().userDetails?.clinic ?? null;

/**
 * The staff member a performer's login belongs to. A staff record carries its login as `user`.
 *
 * @param {string} userId - the login an item records as its performer
 * @returns {Object|null} the staff record
 */
export const staffMemberForLogin = (userId) => useClientsStore().staffMembers
  .find((staffMember) => staffMember.user === userId) ?? null;

/**
 * The calendar date a session was billed on, from one of its own items, so it reads the way the
 * billing items list shows it rather than in the viewer's timezone.
 *
 * @param {string} clinicId
 * @param {string} sessionActivityId
 * @returns {Promise<string|null>} 'YYYY-MM-DD'
 */
export async function sessionServiceDate(clinicId, sessionActivityId) {
  const [item] = await new BillingItemsDB().readAll(
    [['clinicId', '==', clinicId], ['sessionActivityId', '==', sessionActivityId]],
    null,
    null,
    1,
  );

  return item?.serviceDate ?? null;
} // sessionServiceDate()

/** Read once per session, since a worklist asks after the same sessions on every row. */
const sessionDates = createLookup({
  read: (key) => {
    const [clinicId, sessionActivityId] = key.split('|');

    return sessionServiceDate(clinicId, sessionActivityId);
  },
  label: 'a session date for a hold task',
});

/**
 * A session's billed date as a screen reads it: undefined while first read, then the date, or null
 * when none was found, which is asked again after a minute.
 *
 * @param {string} clinicId
 * @param {string} sessionActivityId
 * @returns {string|null|undefined} 'YYYY-MM-DD'
 */
export const sessionDateOf = (clinicId, sessionActivityId) => sessionDates.get(`${clinicId}|${sessionActivityId}`);
